<template>
  <v-app id="inspire" class="app dashboard">
    <v-content>
      <nav>
        <v-toolbar class>
          <v-app-bar-nav-icon
            @click.stop="drawer = !drawer"
          ></v-app-bar-nav-icon>
          <v-subheader class="header-info">{{ settings.name }}</v-subheader>
          <v-spacer></v-spacer>
          <v-menu bottom left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon color="primary" v-bind="attrs" v-on="on">
                <v-icon>settings</v-icon>
              </v-btn>
            </template>
            <v-list>
              <div v-for="item in settingsMenu" :key="item.title">
                <div v-if="item.children && item.children.length">
                  <v-list-group :prepend-icon="item.icon" class="settings">
                    <template v-slot:activator>
                      <v-list-item-title>{{ item.title }}</v-list-item-title>
                    </template>
                    <div v-for="child in item.children" :key="child.title">
                      <v-list-item
                        v-if="child.visible"
                        @click="navigate(child.path)"
                        link
                        color="primary"
                      >
                        <v-list-item-icon>
                          <v-icon>{{ child.icon }}</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>{{ child.title }}</v-list-item-title>
                      </v-list-item>
                    </div>
                  </v-list-group>
                </div>
                <div v-else>
                  <v-list-item
                    v-if="item.visible"
                    @click="navigate(item.path)"
                    link
                    color="primary"
                  >
                    <v-list-item-icon>
                      <v-icon>{{ item.icon }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>{{ item.title }}</v-list-item-title>
                  </v-list-item>
                </div>
              </div>
            </v-list>
          </v-menu>
        </v-toolbar>
      </nav>
      <sidebar :expanded.sync="drawer" @open-modal="openModal" />
      <div>
        <page-title :title="pageTitle" class="mb-4" />
        <router-view class="ml-4 mr-2" />
      </div>
      <invite-associate-modal
        v-if="inviteAssociateModal"
        :title="inviteAssociateModalTitle"
        :inviteAssociateModal.sync="inviteAssociateModal"
        @invite="inviteAssociate"
      />
      <user-modal
        v-if="userModal"
        :title="userModalTitle"
        :userModal.sync="userModal"
        :customers="customers"
        :permissions="permissions"
        action="invite"
        selectCustomer
        @save="saveUser"
      />

      <v-snackbar
        v-model="snack.show"
        :color="snack.color"
        :right="true"
        :top="true"
        :timeout="3000"
      >
        {{ snack.text }}
        <v-btn dark text @click="snack.show = false">Close</v-btn>
      </v-snackbar>
    </v-content>
  </v-app>
</template>

<script>
import Sidebar from "@/components/Sidebar";
import { get } from "lodash";
import InviteAssociateModal from "@/components/shared/modals/InviteAssociateModal";
import UserModal from "@/components/shared/modals/UserModal";
import request from "@/modules/request";
import {
  associateQuickActions,
  userQuickActions,
} from "@/modules/quickActions";
import { errorMapper } from "@/modules/errorMessages";
import PageTitle from "@/components/shared/PageTitle";
import { associateSettings, userSettings } from "../../modules/settings";
import settingsMixin from "../../mixins/settingsMixin";

export default {
  name: "DefaultLayout",
  mixins: [settingsMixin],
  provide: function () {
    return {
      snackBar: this.snackBar,
    };
  },
  components: {
    Sidebar,
    InviteAssociateModal,
    UserModal,
    PageTitle,
  },
  reactiveProvide: {
    name: "reactiveInjected",
    include: ["activeUser"],
  },
  mounted() {
    this.setPageTitle();
    this.getCustomers();
    this.getPermissions();
  },
  computed: {
    settingsMenu() {
      const { isAssociate } = this;
      const { activeUser: user } = this.reactiveInjected;
      return isAssociate ? associateSettings(user) : userSettings(user);
    },
    quickActions() {
      const {
        isAssociate,
        isUser,
        associateQuickActions,
        userQuickActions,
        activeUser,
      } = this;
      return isAssociate
        ? associateQuickActions(activeUser)
        : isUser
        ? userQuickActions(activeUser)
        : [];
    },
    menu() {
      const { isAssociate, isUser, associateMenu, userMenu } = this;
      return isAssociate
        ? associateQuickActions
        : isUser
        ? userQuickActions
        : [];
    },
    isAssociate() {
      const userType = get(this, "activeUser.userType");
      return userType === "associate";
    },
    isUser() {
      const userType = get(this, "activeUser.userType");
      return userType === "user";
    },
  },
  data() {
    return {
      userModal: false,
      userModalTitle: "Add User",
      inviteAssociateModal: false,
      inviteAssociateModalTitle: "Add Associate",
      pageTitle: null,
      drawer: true,
      customers: [],
      activeUser: JSON.parse(localStorage.getItem("activeUser")),
      associateQuickActions,
      userQuickActions,
      snack: {
        show: false,
        text: "",
      },
      fun: true,
      permissions: [],
    };
  },
  methods: {
    navigate(path) {
      if (path === "AssociateLogin") {
        localStorage.removeItem("AssociateLogin");
      }
      this.$router.push({ name: path });
    },
    handleClick(action) {
      const { event, params } = action;
      if (event === "navigate") {
        const { path } = params;
        this.$router.push({ name: path });
      } else if (event === "open-modal") {
        const { modal } = params;
        this.$emit("open-modal", modal);
      }
    },
    openModal(modal) {
      this[modal] = true;
    },
    snackBar(text, color) {
      this.snack = {
        show: true,
        text,
        color,
      };
    },
    async getPermissions() {
      await request
        .callInternal({
          method: "get",
          route: `/api/admin/permissions`,
        })
        .then((results) => {
          this.permissions = results.data;
        })
        .catch((error) => {
          console.error(error);
          return;
        });
    },
    async inviteAssociate(associate) {
      return request
        .callInternal({
          method: "post",
          route: "/api/admin/inviteassociate",
          data: associate,
        })
        .then((results) => {
          this.$swal({
            title: "Congratulations!",
            text: `${associate.firstName} has been invited. ${associate.firstName} will receive instructions on how to complete the registration process.`,
            icon: "success",
          });
          this.inviteAssociateModal = false;
        })
        .catch((error) => {
          const errorMessage = errorMapper["duplicate-associate-email"](
            associate
          );
          this.$swal({
            title: "Error!",
            text: errorMessage,
            icon: "error",
          });
        });
    },
    getCustomers() {
      // TODO - only call getCustomers if user selects the user modal option
      return request
        .callInternal({
          method: "get",
          route: "/api/admin/companies",
        })
        .then((results) => {
          this.customers = results.data;
        })
        .catch((error) => {
          console.error(error);
        });
    },
    async saveUser(user) {
      await request
        .callInternal({
          method: "post",
          route: `/api/admin/inviteuser`,
          data: { user },
        })
        .then(() => {
          this.$swal({
            title: "Congratulations!",
            text: `${user.firstName} has been added. ${user.firstName} will receive instructions on how to complete the registration process.`,
            icon: "success",
          });
        })
        .catch((error) => {
          const errorMessage = errorMapper["duplicate-user-email"](user);
          this.$swal({
            title: "Error!",
            text: errorMessage,
            icon: "error",
          });
        });
      this.userModal = false;
    },
    setPageTitle() {
      this.pageTitle = get(this, "$route.meta.pageTitle");
    },
  },
  watch: {
    $route(to, from) {
      this.setPageTitle();
    },
  },
};
</script>

<style scoped>
.page-wrapper {
  min-height: calc(100vh - 64px - 50px - 81px);
}
.page-title {
  font-family: "Roboto", sans-serif;
  font-size: 24px;
}
</style>
